import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';
import '../AccountStyles.css';

function TrialBalanceScreen() {
  const navigate = useNavigate();
  const [trialBalance, setTrialBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    fetchTrialBalance();
  }, [asOfDate]);

  const fetchTrialBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/trial-balance?asOfDate=${asOfDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrialBalance(data);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching trial balance');
      }
    } catch (error) {
      console.error('Error fetching trial balance:', error);
      setError('Error fetching trial balance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setAsOfDate(e.target.value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const calculateTotals = () => {
    const totalDebits = trialBalance.reduce((sum, account) => sum + (account.debit_balance || 0), 0);
    const totalCredits = trialBalance.reduce((sum, account) => sum + (account.credit_balance || 0), 0);
    return { totalDebits, totalCredits };
  };

  if (loading) {
    return (
      <Container fluid className="account-screen">
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  const { totalDebits, totalCredits } = calculateTotals();

  return (
    <Container fluid className="account-screen p-0">
      <Row className="g-0">
        {/* Main Content */}
        <Col xs={12} className="main-content">
          <div style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
            <Row>
              <Col>
                {/* Header */}
                <Card className="mb-3">
                  <Card.Body>
                    <h1 className="mb-0">Trial Balance</h1>
                    <p className="text-muted mb-0">
                      As of: {new Date(asOfDate).toLocaleDateString()}
                    </p>
                  </Card.Body>
                </Card>

                {error && (
                  <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
                    {error}
                  </Alert>
                )}

                {/* Date Selection */}
                <Card className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <label htmlFor="asOfDate" className="form-label">As of Date:</label>
                        <input
                          type="date"
                          className="form-control"
                          id="asOfDate"
                          value={asOfDate}
                          onChange={handleDateChange}
                        />
                      </Col>
                      <Col md={8} className="d-flex align-items-end">
                        <Button variant="primary" onClick={fetchTrialBalance}>
                          Refresh Trial Balance
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Trial Balance Table */}
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Account Number</th>
                          <th>Account Name</th>
                          <th>Account Type</th>
                          <th className="text-end">Debit Balance</th>
                          <th className="text-end">Credit Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trialBalance.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No trial balance data available
                            </td>
                          </tr>
                        ) : (
                          trialBalance.map((account, index) => (
                            <tr key={index}>
                              <td>{account.account_number}</td>
                              <td>{account.description}</td>
                              <td>
                                <span className={`badge ${
                                  account.account_type === 'Asset' ? 'bg-primary' :
                                  account.account_type === 'Liability' ? 'bg-warning' :
                                  account.account_type === 'Equity' ? 'bg-info' :
                                  account.account_type === 'Revenue' ? 'bg-success' :
                                  'bg-secondary'
                                }`}>
                                  {account.account_type}
                                </span>
                              </td>
                              <td className="text-end">
                                {account.debit_balance ? formatCurrency(account.debit_balance) : '-'}
                              </td>
                              <td className="text-end">
                                {account.credit_balance ? formatCurrency(account.credit_balance) : '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="table-dark">
                          <th colSpan="3">TOTALS</th>
                          <th className="text-end">{formatCurrency(totalDebits)}</th>
                          <th className="text-end">{formatCurrency(totalCredits)}</th>
                        </tr>
                      </tfoot>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <style>
        {`
          .main-content {
            padding: 20px;
          }
          
          .badge {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
          }
        `}
      </style>
    </Container>
  );
}

export default TrialBalanceScreen;
